// Enums (based on Swagger)
// NOTE: 'CANCELLED' is NOT part of the API spec; it is a local/offline state
// used by the client to represent a submitted request cancelled before sync.
export type RequestStatus =
    'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'MODIFIED' | 'CANCELLED';

/**
 * `RequestStatus` values for use as runtime values (comparisons, keys, return
 * values). Kept in sync with the `RequestStatus` type above via `satisfies`.
 *
 * Use `RequestStatus.DRAFT` instead of the string literal `'DRAFT'` to get
 * autocompletion, typo-detection and safe refactors.
 */
export const RequestStatus = {
    DRAFT: 'DRAFT',
    SUBMITTED: 'SUBMITTED',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    MODIFIED: 'MODIFIED',
    CANCELLED: 'CANCELLED',
} as const satisfies Record<RequestStatus, RequestStatus>;

export type RequestSource =
    'VACATION' | 'PAID_LEAVE' | 'WISH' | 'HOLIDAYS' | 'WEEKEND';

/**
 * `RequestSource` values for use as runtime values (comparisons, keys, return
 * values). Kept in sync with the `RequestSource` type above via `satisfies`.
 *
 * Use `RequestSource.VACATION` instead of the string literal `'VACATION'`.
 */
export const RequestSource = {
    VACATION: 'VACATION',
    PAID_LEAVE: 'PAID_LEAVE',
    WISH: 'WISH',
    HOLIDAYS: 'HOLIDAYS',
    WEEKEND: 'WEEKEND',
} as const satisfies Record<RequestSource, RequestSource>;

/**
 * UI request categories. The API exposes 5 `RequestSource` values, but the
 * UI groups them into 3 categories:
 * - `VACATION` ← `VACATION`, `HOLIDAYS`, `WEEKEND`
 * - `PAID_LEAVE` ← `PAID_LEAVE`
 * - `SHIFT_PREFERENCE` ← `WISH`
 *
 * Use `RequestType.VACATION` for label/icon/color lookups. `RequestSource` is
 * the canonical value stored on `Request.source`; `RequestType` is the derived
 * UI category (see `sourceToRequestType`).
 */
export type RequestType = 'VACATION' | 'PAID_LEAVE' | 'SHIFT_PREFERENCE';

export const RequestType = {
    VACATION: 'VACATION',
    PAID_LEAVE: 'PAID_LEAVE',
    SHIFT_PREFERENCE: 'SHIFT_PREFERENCE',
} as const satisfies Record<RequestType, RequestType>;

/**
 * `SegmentType` values for use as runtime values (comparisons, keys, return
 * values). Kept in sync with the `SegmentType` type above via `satisfies`.
 *
 * The keys are the semantic shift names (MORNING/AFTERNOON/...) that the UI
 * uses for label/icon/color lookups; the values are the API segment codes
 * ('D'/'A'/...). Use `SegmentType.MORNING` instead of the string literal
 * `'D'` to get autocompletion, typo-detection and safe refactors.
 *
 * This also replaces the legacy `ShiftPreference` enum in the requests feature.
 */
export type SegmentType = 'D' | 'A' | 'N' | 'L' | 'X';

export const SegmentType = {
    MORNING: 'D',
    AFTERNOON: 'A',
    NIGHT: 'N',
    WEEKEND: 'L',
    OTHER: 'X',
} as const satisfies Record<string, SegmentType>;
export type TransactionStatus =
    | 'CREATED'
    | 'COMMENTED'
    | 'COMMENTED_BY_LEADER'
    | 'SUBMITTED'
    | 'APPROVED'
    | 'REJECTED'
    | 'MODIFIED';

// Error codes from OpenAPI
export type ErrorCode =
    | 'GENERAL_ERROR'
    | 'BAD_REQUEST'
    | 'UNAUTHORIZED'
    | 'INVALID_CHARACTERS'
    | 'ILLEGAL_ARGUMENT'
    | 'MODULE_NOT_ENABLED'
    | 'ORG_EMPLOYEE_NOT_FOUND'
    | 'ORG_EMPLOYEE_ACCESS_FORBIDDEN'
    | 'EMPLOYEE_NOT_FOUND'
    | 'EMPLOYEE_ACCESS_FORBIDDEN'
    | 'ORG_NOT_FOUND'
    | 'REQUEST_NOT_FOUND'
    | 'REQUEST_ACCESS_FORBIDDEN'
    | 'REQUEST_DEADLINE_EXPIRED'
    | 'REQUEST_LOCKED';

export interface ErrorResponseErrorCode {
    errorCode: ErrorCode;
    description: string;
}

export interface ApiErrorResponse {
    body: ErrorResponseErrorCode;
    status: number;
}

// AvailabilityPattern (used in PUT - without id, registered, updated)
export interface PutAvailabilityPattern {
    summary?: string;
    segmentPattern: SegmentType[][];
    segmentPatternId?: string;
    startPeriods?: string[]; // format: date
    excludePeriods?: string[]; // format: date
    series?: AvailabilityPatternSeries;
    disabled?: boolean;
    optimize?: boolean;
}

// AvailabilityPattern (used in GET - with id, registered, updated)
export interface RestAvailabilityPattern extends PutAvailabilityPattern {
    id: string;
    registered: string; // format: date-time
    updated: string; // format: date-time
}

// Alias for backward compatibility
export type AvailabilityPattern =
    RestAvailabilityPattern | PutAvailabilityPattern;

export interface AvailabilityPatternSeries {
    toggled: boolean;
    from?: string; // format: date
    until?: string; // format: date
    period?: string;
}

// RequestTransaction
export interface RequestTransaction {
    id: string;
    registered: string; // format: date-time
    comment?: string;
    status: TransactionStatus;
    authId: string;
}

// BaseRequest (common for Employee and OrgEmployee)
export interface BaseRequest {
    id: string;
    registered: string; // format: date-time
    updated: string; // format: date-time
    availabilityPattern: RestAvailabilityPattern;
    source: RequestSource;
    deadline?: string; // format: date-time
    status: RequestStatus;
    transactions: RequestTransaction[];
}

// Specific requests
export interface EmployeeRequest extends BaseRequest {
    employeeId: string;
}

export interface OrgEmployeeRequest extends BaseRequest {
    orgEmployeeId: string;
}

// List responses
export interface EmployeeRequestsList {
    elements: EmployeeRequest[];
}

export interface OrgEmployeeRequestsList {
    elements: OrgEmployeeRequest[];
}

// DTO for PUT comment
export interface PutCommentRequest {
    value: string;
}

// ApiResponse wrapper (for API responses)
export interface ApiResponse<T> {
    body: T;
    status: number;
}

export interface Org {
    id: string;
    name: string;
}

// Specific response types
export type OrgEmployeeRequestResponse = ApiResponse<OrgEmployeeRequest>;
export type EmployeeRequestResponse = ApiResponse<EmployeeRequest>;
export type OrgEmployeeRequestsResponse = ApiResponse<OrgEmployeeRequestsList>;
export type EmployeeRequestsResponse = ApiResponse<EmployeeRequestsList>;

// Custom error for HTTP client
export interface HttpError extends Error {
    status: number;
    errorCode?: ErrorCode;
    description?: string;
}
